import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Search, Briefcase, MapPin, Clock, DollarSign, Building2, Plus, ExternalLink,
  Mail, Phone, Globe, X, Loader2, Users, Calendar, Bell, Trash2,
  Filter, List, Map, Navigation, Bookmark, BookmarkCheck, Pencil, AlertTriangle
} from "lucide-react";
import {
  getJobs, getJobCategories, getJobTypes, getMyBusinessListings, createJob,
  deleteJob, updateJob, applyForJob, getMyJobs, getJobAlerts, createJobAlert,
  deleteJobAlert, getJobMapListings, toggleBookmark, checkBookmarks, geocodeAddress
} from "@/lib/api";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const JOB_TYPE_COLORS = {
  "full-time": "bg-green-100 text-green-800 border-green-200",
  "part-time": "bg-blue-100 text-blue-800 border-blue-200",
  "contract": "bg-orange-100 text-orange-800 border-orange-200",
  "internship": "bg-purple-100 text-purple-800 border-purple-200",
  "volunteer": "bg-[var(--liturgical-secondary)]/20 text-[#8B6914] border-[var(--liturgical-secondary)]/30",
};

function formatSalary(min, max, period) {
  if (!min && !max) return null;
  const fmt = (n) => n >= 1000 ? `$${(n / 1000).toFixed(0)}k` : `$${n}`;
  const p = period === "hourly" ? "/hr" : period === "monthly" ? "/mo" : "/yr";
  if (min && max) return `${fmt(min)} - ${fmt(max)}${p}`;
  if (min) return `From ${fmt(min)}${p}`;
  return `Up to ${fmt(max)}${p}`;
}

// ── Map helpers ────────────────────────────────────────────────────────
const jobIcon = L.divIcon({
  className: "custom-marker",
  html: `<div><svg width="28" height="36" viewBox="0 0 32 40" fill="none"><path d="M16 0C7.163 0 0 7.163 0 16C0 28 16 40 16 40C16 40 32 28 32 16C32 7.163 24.837 0 16 0Z" fill="#6366F1"/><circle cx="16" cy="16" r="8" fill="white"/></svg></div>`,
  iconSize: [28, 36], iconAnchor: [14, 36], popupAnchor: [0, -36],
});
const remoteIcon = L.divIcon({
  className: "custom-marker",
  html: `<div><svg width="28" height="36" viewBox="0 0 32 40" fill="none"><path d="M16 0C7.163 0 0 7.163 0 16C0 28 16 40 16 40C16 40 32 28 32 16C32 7.163 24.837 0 16 0Z" fill="#14B8A6"/><circle cx="16" cy="16" r="8" fill="white"/></svg></div>`,
  iconSize: [28, 36], iconAnchor: [14, 36], popupAnchor: [0, -36],
});

function MapEventHandler({ onBoundsChange }) {
  useMapEvents({
    moveend: (e) => {
      const bounds = e.target.getBounds();
      onBoundsChange({
        ne_lat: bounds.getNorthEast().lat, ne_lng: bounds.getNorthEast().lng,
        sw_lat: bounds.getSouthWest().lat, sw_lng: bounds.getSouthWest().lng,
      });
    },
  });
  return null;
}

function CenterOnLocation({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.setView([center.lat, center.lng], 12); }, [center, map]);
  return null;
}

function JobMapPopup({ job }) {
  const salary = formatSalary(job.salary_min, job.salary_max, job.salary_period);
  return (
    <div className="min-w-[200px] p-1">
      <h3 className="font-semibold text-sm font-['Nunito'] text-gray-900">{job.title}</h3>
      <p className="text-xs text-gray-500 font-['Nunito']">{job.business_name}</p>
      <div className="flex items-center gap-1 mt-1 text-xs text-gray-500 font-['Nunito']">
        <MapPin className="w-3 h-3" />{job.location}
      </div>
      <div className="flex flex-wrap gap-1 mt-1.5">
        <Badge variant="outline" className={`text-[10px] capitalize ${JOB_TYPE_COLORS[job.job_type] || ""}`}>{job.job_type}</Badge>
        {job.remote_ok && <Badge variant="outline" className="text-[10px] bg-teal-50 text-teal-700 border-teal-200">Remote</Badge>}
      </div>
      {salary && <p className="text-xs text-gray-600 mt-1 font-['Nunito']"><DollarSign className="w-3 h-3 inline" /> {salary}</p>}
    </div>
  );
}

// ── Create / Edit Job Modal ────────────────────────────────────────────
function JobFormModal({ isOpen, onClose, onSuccess, businesses, editJob }) {
  const isEdit = !!editJob;
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const defaultForm = {
    listing_id: "", title: "", description: "", job_type: "", category: "",
    location: "", remote_ok: false, salary_min: "", salary_max: "",
    salary_period: "yearly", requirements: "", contact_email: "",
    contact_phone: "", application_url: "", expires_days: 30
  };
  const [form, setForm] = useState(defaultForm);

  useEffect(() => {
    if (isOpen) {
      getJobCategories().then(r => setCategories(r.data.categories)).catch(() => {});
      getJobTypes().then(r => setJobTypes(r.data.job_types)).catch(() => {});
      if (editJob) {
        setForm({
          listing_id: editJob.listing_id || "",
          title: editJob.title || "", description: editJob.description || "",
          job_type: editJob.job_type || "", category: editJob.category || "",
          location: editJob.location || "", remote_ok: editJob.remote_ok || false,
          salary_min: editJob.salary_min || "", salary_max: editJob.salary_max || "",
          salary_period: editJob.salary_period || "yearly",
          requirements: editJob.requirements || "",
          contact_email: editJob.contact_email || "", contact_phone: editJob.contact_phone || "",
          application_url: editJob.application_url || "", expires_days: 30
        });
      } else {
        setForm({ ...defaultForm, listing_id: businesses.length === 1 ? businesses[0].listing_id : "" });
      }
    }
  }, [isOpen, editJob, businesses]);

  const handleSubmit = async () => {
    if (!isEdit && !form.listing_id) { toast.error("Please select a business"); return; }
    if (!form.title.trim() || !form.description.trim() || !form.job_type || !form.category || !form.location.trim()) {
      toast.error("Please fill all required fields"); return;
    }
    if (!form.contact_email && !form.contact_phone && !form.application_url) {
      toast.error("Add at least one contact method"); return;
    }
    setLoading(true);
    try {
      if (isEdit) {
        const payload = {
          title: form.title, description: form.description, job_type: form.job_type,
          category: form.category, location: form.location, remote_ok: form.remote_ok,
          salary_min: form.salary_min ? parseInt(form.salary_min) : null,
          salary_max: form.salary_max ? parseInt(form.salary_max) : null,
          salary_period: form.salary_period, requirements: form.requirements || null,
          contact_email: form.contact_email || null, contact_phone: form.contact_phone || null,
          application_url: form.application_url || null,
        };
        await updateJob(editJob.job_id, payload);
        toast.success("Job updated!");
      } else {
        const payload = {
          ...form,
          salary_min: form.salary_min ? parseInt(form.salary_min) : null,
          salary_max: form.salary_max ? parseInt(form.salary_max) : null,
          expires_days: parseInt(form.expires_days) || 30
        };
        await createJob(payload);
        toast.success("Job posted!");
      }
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${isEdit ? "update" : "post"} job`);
    }
    setLoading(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-xl">{isEdit ? "Edit Job" : "Post a Job"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {!isEdit && businesses.length > 1 && (
            <div>
              <Label className="font-['Nunito'] text-sm">Business *</Label>
              <Select value={form.listing_id} onValueChange={v => setForm(f => ({ ...f, listing_id: v }))}>
                <SelectTrigger className="border-[#E5E0D5]" data-testid="job-business-select"><SelectValue placeholder="Select business" /></SelectTrigger>
                <SelectContent className="bg-white">{businesses.map(b => <SelectItem key={b.listing_id} value={b.listing_id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="font-['Nunito'] text-sm">Job Title *</Label>
            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Parish Secretary" className="border-[#E5E0D5]" data-testid="job-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-['Nunito'] text-sm">Type *</Label>
              <Select value={form.job_type} onValueChange={v => setForm(f => ({ ...f, job_type: v }))}>
                <SelectTrigger className="border-[#E5E0D5]" data-testid="job-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white">{jobTypes.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-['Nunito'] text-sm">Category *</Label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger className="border-[#E5E0D5]" data-testid="job-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white">{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="font-['Nunito'] text-sm">Location *</Label>
            <Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g. Austin, TX" className="border-[#E5E0D5]" data-testid="job-location-input" />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.remote_ok} onCheckedChange={v => setForm(f => ({ ...f, remote_ok: v }))} data-testid="job-remote-switch" />
            <Label className="font-['Nunito'] text-sm">Remote work available</Label>
          </div>
          <div>
            <Label className="font-['Nunito'] text-sm">Description *</Label>
            <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Job description, responsibilities..." className="min-h-[100px] border-[#E5E0D5]" data-testid="job-description-input" />
          </div>
          <div>
            <Label className="font-['Nunito'] text-sm">Requirements</Label>
            <Textarea value={form.requirements} onChange={e => setForm(f => ({ ...f, requirements: e.target.value }))} placeholder="Required qualifications..." className="min-h-[60px] border-[#E5E0D5]" data-testid="job-requirements-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="font-['Nunito'] text-sm">Salary Min</Label>
              <Input type="number" value={form.salary_min} onChange={e => setForm(f => ({ ...f, salary_min: e.target.value }))} placeholder="40000" className="border-[#E5E0D5]" data-testid="job-salary-min" />
            </div>
            <div>
              <Label className="font-['Nunito'] text-sm">Salary Max</Label>
              <Input type="number" value={form.salary_max} onChange={e => setForm(f => ({ ...f, salary_max: e.target.value }))} placeholder="60000" className="border-[#E5E0D5]" data-testid="job-salary-max" />
            </div>
            <div>
              <Label className="font-['Nunito'] text-sm">Period</Label>
              <Select value={form.salary_period} onValueChange={v => setForm(f => ({ ...f, salary_period: v }))}>
                <SelectTrigger className="border-[#E5E0D5]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="yearly">Yearly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="hourly">Hourly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2 border-t border-[#E5E0D5] pt-3">
            <Label className="font-['Nunito'] text-sm font-semibold">Contact (at least one) *</Label>
            <Input value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} placeholder="Email" className="border-[#E5E0D5]" data-testid="job-contact-email" />
            <Input value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))} placeholder="Phone" className="border-[#E5E0D5]" data-testid="job-contact-phone" />
            <Input value={form.application_url} onChange={e => setForm(f => ({ ...f, application_url: e.target.value }))} placeholder="Application URL" className="border-[#E5E0D5]" data-testid="job-application-url" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E5E0D5]">Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white" data-testid="job-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {isEdit ? "Save Changes" : "Post Job"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete confirm modal ──────────────────────────────────────────────
function DeleteJobModal({ job, isOpen, onClose, onConfirm }) {
  const [loading, setLoading] = useState(false);
  if (!job) return null;
  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteJob(job.job_id);
      toast.success("Job deleted");
      onConfirm();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete job");
    }
    setLoading(false);
  };
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />Delete Job
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#6B665E] font-['Nunito']">
          Are you sure you want to delete <strong>{job.title}</strong>? This cannot be undone.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E5E0D5]">Cancel</Button>
          <Button onClick={handleDelete} disabled={loading} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-job-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Job Card ──────────────────────────────────────────────────────────
function JobCard({ job, onApply, isOwner, onEdit, onDelete, isBookmarked, onToggleBookmark, user }) {
  const salary = formatSalary(job.salary_min, job.salary_max, job.salary_period);
  const typeColor = JOB_TYPE_COLORS[job.job_type] || "bg-gray-100 text-gray-800";
  const daysAgo = Math.floor((Date.now() - new Date(job.created_at).getTime()) / 86400000);

  return (
    <Card className="bg-white border-[#E5E0D5] hover:shadow-md transition-shadow" data-testid={`job-card-${job.job_id}`}>
      <CardContent className="p-5">
        <div className="flex gap-4">
          {job.business_photo ? (
            <img src={job.business_photo} alt="" className="w-12 h-12 rounded-lg object-cover border border-[#E5E0D5] flex-shrink-0" />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-[var(--liturgical-primary)]/10 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-6 h-6 text-[var(--liturgical-primary)]" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] leading-tight">{job.title}</h3>
                <p className="text-sm text-[#6B665E] font-['Nunito'] mt-0.5">{job.business_name}</p>
              </div>
              {/* Bookmark button */}
              {user && !isOwner && (
                <Button
                  variant="ghost" size="sm"
                  onClick={e => { e.stopPropagation(); onToggleBookmark(job.job_id); }}
                  className={`h-8 w-8 p-0 flex-shrink-0 ${isBookmarked ? "text-[var(--liturgical-primary)]" : "text-[#9C9689] hover:text-[var(--liturgical-primary)]"}`}
                  data-testid={`bookmark-job-${job.job_id}`}
                >
                  {isBookmarked ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                </Button>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              <Badge variant="outline" className={`text-xs font-['Nunito'] capitalize ${typeColor}`}>{job.job_type}</Badge>
              <span className="text-xs text-[#6B665E] font-['Nunito'] flex items-center gap-1"><MapPin className="w-3 h-3" />{job.location}</span>
              {job.remote_ok && <Badge variant="outline" className="text-xs font-['Nunito'] bg-teal-50 text-teal-700 border-teal-200">Remote OK</Badge>}
              {salary && <span className="text-xs text-[#6B665E] font-['Nunito'] flex items-center gap-1"><DollarSign className="w-3 h-3" />{salary}</span>}
              {job.distance_formatted && (
                <Badge className="bg-[var(--liturgical-primary)] text-white text-[10px] font-['Nunito']">{job.distance_formatted}</Badge>
              )}
            </div>
            <p className="text-sm text-[#6B665E] font-['Nunito'] mt-2 line-clamp-2">{job.description}</p>
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-[#9C9689] font-['Nunito']">
                {daysAgo === 0 ? "Today" : daysAgo === 1 ? "Yesterday" : `${daysAgo}d ago`}
                {job.application_count > 0 && ` · ${job.application_count} interested`}
              </span>
              <div className="flex gap-2">
                {isOwner && (
                  <>
                    <Button size="sm" variant="outline" onClick={e => { e.stopPropagation(); onEdit(job); }}
                      className="text-xs h-7 px-2 border-[#E5E0D5] font-['Nunito']" data-testid={`edit-job-${job.job_id}`}>
                      <Pencil className="w-3 h-3 mr-1" />Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={e => { e.stopPropagation(); onDelete(job); }}
                      className="text-xs h-7 px-2 border-red-200 text-red-600 hover:bg-red-50 font-['Nunito']" data-testid={`delete-job-${job.job_id}`}>
                      <Trash2 className="w-3 h-3 mr-1" />Delete
                    </Button>
                  </>
                )}
                {!isOwner && (
                  <Button size="sm" onClick={e => { e.stopPropagation(); onApply(job); }}
                    className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white text-xs h-7 px-3 font-['Nunito']" data-testid={`apply-job-${job.job_id}`}>
                    Apply
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Job Detail Modal ──────────────────────────────────────────────────
function JobDetailModal({ job, isOpen, onClose, onApply, isOwner, onEdit, onDelete, isBookmarked, onToggleBookmark, user }) {
  if (!job) return null;
  const salary = formatSalary(job.salary_min, job.salary_max, job.salary_period);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex gap-3 items-start">
            {job.business_photo ? (
              <img src={job.business_photo} alt="" className="w-12 h-12 rounded-lg object-cover border border-[#E5E0D5]" />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-[var(--liturgical-primary)]/10 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-[var(--liturgical-primary)]" />
              </div>
            )}
            <div className="flex-1">
              <DialogTitle className="font-['Crimson_Text'] text-xl">{job.title}</DialogTitle>
              <p className="text-sm text-[#6B665E] font-['Nunito']">{job.business_name}</p>
            </div>
            {user && !isOwner && (
              <Button variant="ghost" size="sm" onClick={() => onToggleBookmark(job.job_id)}
                className={`h-8 w-8 p-0 ${isBookmarked ? "text-[var(--liturgical-primary)]" : "text-[#9C9689]"}`}
                data-testid="job-detail-bookmark-btn">
                {isBookmarked ? <BookmarkCheck className="w-5 h-5" /> : <Bookmark className="w-5 h-5" />}
              </Button>
            )}
          </div>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className={`capitalize ${JOB_TYPE_COLORS[job.job_type] || ""}`}>{job.job_type}</Badge>
            <Badge variant="outline" className="bg-[#F9F7F2] border-[#E5E0D5]">{job.category}</Badge>
            {job.remote_ok && <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200">Remote OK</Badge>}
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm font-['Nunito']">
            <div className="flex items-center gap-2 text-[#6B665E]"><MapPin className="w-4 h-4" />{job.location}</div>
            {salary && <div className="flex items-center gap-2 text-[#6B665E]"><DollarSign className="w-4 h-4" />{salary}</div>}
            <div className="flex items-center gap-2 text-[#6B665E]"><Calendar className="w-4 h-4" />Expires {new Date(job.expires_at).toLocaleDateString()}</div>
            {job.application_count > 0 && <div className="flex items-center gap-2 text-[#6B665E]"><Users className="w-4 h-4" />{job.application_count} interested</div>}
            {job.distance_formatted && <div className="flex items-center gap-2 text-[var(--liturgical-primary)]"><Navigation className="w-4 h-4" />{job.distance_formatted}</div>}
          </div>
          <div>
            <h4 className="font-['Crimson_Text'] font-semibold text-[#2D2A26] mb-1">Description</h4>
            <p className="text-sm text-[#6B665E] font-['Nunito'] whitespace-pre-line">{job.description}</p>
          </div>
          {job.requirements && (
            <div>
              <h4 className="font-['Crimson_Text'] font-semibold text-[#2D2A26] mb-1">Requirements</h4>
              <p className="text-sm text-[#6B665E] font-['Nunito'] whitespace-pre-line">{job.requirements}</p>
            </div>
          )}
          <div className="border-t border-[#E5E0D5] pt-3 space-y-2">
            <h4 className="font-['Crimson_Text'] font-semibold text-[#2D2A26]">How to Apply</h4>
            {job.contact_email && <a href={`mailto:${job.contact_email}`} className="flex items-center gap-2 text-sm text-[var(--liturgical-primary)] hover:underline font-['Nunito']"><Mail className="w-4 h-4" />{job.contact_email}</a>}
            {job.contact_phone && <a href={`tel:${job.contact_phone}`} className="flex items-center gap-2 text-sm text-[var(--liturgical-primary)] hover:underline font-['Nunito']"><Phone className="w-4 h-4" />{job.contact_phone}</a>}
            {job.application_url && <a href={job.application_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[var(--liturgical-primary)] hover:underline font-['Nunito']"><ExternalLink className="w-4 h-4" />Apply Online</a>}
          </div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          {isOwner && (
            <>
              <Button variant="outline" onClick={() => { onEdit(job); onClose(); }}
                className="border-[#E5E0D5] font-['Nunito']" data-testid="job-detail-edit-btn">
                <Pencil className="w-4 h-4 mr-1.5" />Edit
              </Button>
              <Button variant="outline" onClick={() => { onDelete(job); onClose(); }}
                className="border-red-200 text-red-600 hover:bg-red-50 font-['Nunito']" data-testid="job-detail-delete-btn">
                <Trash2 className="w-4 h-4 mr-1.5" />Delete
              </Button>
            </>
          )}
          {!isOwner && (
            <Button onClick={() => { onApply(job); onClose(); }}
              className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']" data-testid="job-detail-apply-btn">
              Express Interest
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════
export default function JobBoardPage({ user }) {
  const [searchParams] = useSearchParams();

  // Data
  const [jobs, setJobs] = useState([]);
  const [mapJobs, setMapJobs] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapLoading, setMapLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [businesses, setBusinesses] = useState([]);

  // Filters
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get("category") || "all");
  const [typeFilter, setTypeFilter] = useState(searchParams.get("type") || "all");
  const [remoteFilter, setRemoteFilter] = useState(searchParams.get("remote") === "1");
  const [sortBy, setSortBy] = useState("newest");
  const [showFilters, setShowFilters] = useState(
    !!(searchParams.get("category") || searchParams.get("type") || searchParams.get("location") || searchParams.get("remote"))
  );

  // Location / distance
  const [userLocation, setUserLocation] = useState(null);
  const [locationQuery, setLocationQuery] = useState(searchParams.get("location") || "");
  const [maxDistance, setMaxDistance] = useState("25");
  const [gettingLocation, setGettingLocation] = useState(false);
  const [geocoding, setGeocoding] = useState(false);

  // View state
  const [viewMode, setViewMode] = useState("list");
  const [activeTab, setActiveTab] = useState("browse");
  const [mapBounds, setMapBounds] = useState(null);

  // Modals
  const [showForm, setShowForm] = useState(false);
  const [editJob, setEditJob] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);

  // Alerts
  const [alerts, setAlerts] = useState([]);
  const [savingAlert, setSavingAlert] = useState(false);

  // Bookmarks
  const [bookmarkedJobIds, setBookmarkedJobIds] = useState(new Set());

  // ── Loaders ──────────────────────────────────────────────────────────
  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (categoryFilter !== "all") params.category = categoryFilter;
      if (typeFilter !== "all") params.job_type = typeFilter;
      if (remoteFilter) params.remote_ok = true;
      if (userLocation) {
        params.lat = userLocation.lat;
        params.lng = userLocation.lng;
        params.max_distance = parseInt(maxDistance);
        params.sort = sortBy === "newest" ? "distance" : sortBy;
      } else {
        params.sort = sortBy;
      }
      const res = await getJobs(params);
      setJobs(res.data.jobs);
      setTotal(res.data.total);

      // Check bookmarks for loaded jobs
      if (user && res.data.jobs.length > 0) {
        const jobIds = res.data.jobs.map(j => j.job_id).join(",");
        try {
          const bRes = await checkBookmarks("", "", jobIds);
          setBookmarkedJobIds(new Set(bRes.data.jobs || []));
        } catch {}
      }
    } catch {}
    setLoading(false);
  }, [search, categoryFilter, typeFilter, remoteFilter, userLocation, maxDistance, sortBy, user]);

  const loadMapJobs = useCallback(async () => {
    setMapLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (categoryFilter !== "all") params.category = categoryFilter;
      if (typeFilter !== "all") params.job_type = typeFilter;
      if (remoteFilter) params.remote_ok = true;
      if (mapBounds) Object.assign(params, mapBounds);
      const res = await getJobMapListings(params);
      setMapJobs(res.data.jobs || []);
    } catch {}
    setMapLoading(false);
  }, [search, categoryFilter, typeFilter, remoteFilter, mapBounds]);

  const loadMyJobs = useCallback(async () => {
    if (!user) return;
    try {
      const res = await getMyJobs();
      setMyJobs(res.data.jobs || []);
    } catch {}
  }, [user]);

  // Initial loads
  useEffect(() => { loadJobs(); }, [loadJobs]);
  useEffect(() => { if (viewMode === "map") loadMapJobs(); }, [viewMode, loadMapJobs]);
  useEffect(() => {
    getJobCategories().then(r => setCategories(r.data.categories)).catch(() => {});
    getJobTypes().then(r => setJobTypes(r.data.job_types)).catch(() => {});
    if (user) {
      getMyBusinessListings().then(r => setBusinesses(r.data.listings || [])).catch(() => {});
      loadMyJobs();
      getJobAlerts().then(r => setAlerts(r.data.alerts || [])).catch(() => {});
    }
  }, [user, loadMyJobs]);

  const myJobIds = useMemo(() => new Set(myJobs.map(j => j.job_id)), [myJobs]);

  // ── Location helpers ────────────────────────────────────────────────
  const handleGetLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude, source: "gps" });
        setLocationQuery("Current Location");
        setGettingLocation(false);
        toast.success("Location detected!");
      },
      () => { setGettingLocation(false); toast.error("Unable to get location"); },
      { timeout: 10000 }
    );
  };

  const handleGeocode = async () => {
    if (!locationQuery.trim() || locationQuery === "Current Location") return;
    setGeocoding(true);
    try {
      const res = await geocodeAddress(locationQuery.trim());
      setUserLocation({ lat: res.data.lat, lng: res.data.lng, source: "geocode", display_name: res.data.display_name || locationQuery });
      toast.success("Location found!");
    } catch {
      toast.error("Could not find that address");
    }
    setGeocoding(false);
  };

  const clearLocation = () => { setUserLocation(null); setLocationQuery(""); };

  // ── Actions ─────────────────────────────────────────────────────────
  const handleApply = async (job) => {
    try {
      await applyForJob(job.job_id);
      toast.success("Interest recorded!");
      loadJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply");
    }
  };

  const handleToggleBookmark = async (jobId) => {
    try {
      const res = await toggleBookmark({ content_type: "job", content_id: jobId });
      setBookmarkedJobIds(prev => {
        const next = new Set(prev);
        if (res.data.bookmarked) { next.add(jobId); toast.success("Job saved"); }
        else { next.delete(jobId); toast.success("Bookmark removed"); }
        return next;
      });
    } catch (err) {
      toast.error("Failed to bookmark");
    }
  };

  const clearFilters = () => {
    setSearch(""); setCategoryFilter("all"); setTypeFilter("all"); setRemoteFilter(false);
    clearLocation(); setSortBy("newest");
  };

  const hasFilters = search || categoryFilter !== "all" || typeFilter !== "all" || remoteFilter || userLocation;

  const handleSaveAlert = async () => {
    setSavingAlert(true);
    try {
      const payload = {};
      if (search) payload.search = search;
      if (categoryFilter !== "all") payload.category = categoryFilter;
      if (typeFilter !== "all") payload.job_type = typeFilter;
      if (locationQuery && locationQuery !== "Current Location") payload.location = locationQuery;
      if (remoteFilter) payload.remote_ok = true;
      const res = await createJobAlert(payload);
      setAlerts(prev => [res.data.alert, ...prev]);
      toast.success("Alert saved!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save alert");
    }
    setSavingAlert(false);
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await deleteJobAlert(alertId);
      setAlerts(prev => prev.filter(a => a.alert_id !== alertId));
      toast.success("Alert removed");
    } catch { toast.error("Failed to delete alert"); }
  };

  const applyAlertFilters = (alert) => {
    setSearch(alert.search || "");
    setCategoryFilter(alert.category || "all");
    setTypeFilter(alert.job_type || "all");
    setLocationQuery(alert.location || "");
    setRemoteFilter(!!alert.remote_ok);
    setShowFilters(true);
  };

  const onFormSuccess = () => { loadJobs(); loadMyJobs(); if (viewMode === "map") loadMapJobs(); };

  // ── Map markers ─────────────────────────────────────────────────────
  const mapMarkers = useMemo(
    () => mapJobs.filter(j => j.coordinates?.lat && j.coordinates?.lng).map(j => ({
      ...j,
      position: [j.coordinates.lat, j.coordinates.lng],
      icon: j.remote_ok ? remoteIcon : jobIcon,
    })),
    [mapJobs]
  );

  const mapCenter = useMemo(() => {
    if (userLocation) return [userLocation.lat, userLocation.lng];
    if (mapMarkers.length > 0) return mapMarkers[0].position;
    return [39.8283, -98.5795];
  }, [userLocation, mapMarkers]);

  // ═══════════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="job-board-page">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-['Crimson_Text'] text-3xl font-bold text-[#2D2A26]">Job Board</h1>
            <p className="text-sm text-[#6B665E] font-['Nunito'] mt-1">
              {total} {total === 1 ? "opportunity" : "opportunities"} from parish businesses
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* View Toggle */}
            <div className="flex items-center bg-white rounded-lg border border-[#E5E0D5] p-1">
              <Button variant={viewMode === "list" ? "default" : "ghost"} size="sm"
                onClick={() => setViewMode("list")}
                className={`font-['Nunito'] text-xs ${viewMode === "list" ? "bg-[var(--liturgical-primary)] text-white" : "text-[#6B665E]"}`}
                data-testid="job-list-view-btn"><List className="w-4 h-4 mr-1" />List</Button>
              <Button variant={viewMode === "map" ? "default" : "ghost"} size="sm"
                onClick={() => setViewMode("map")}
                className={`font-['Nunito'] text-xs ${viewMode === "map" ? "bg-[var(--liturgical-primary)] text-white" : "text-[#6B665E]"}`}
                data-testid="job-map-view-btn"><Map className="w-4 h-4 mr-1" />Map</Button>
            </div>
            {businesses.length > 0 && (
              <Button onClick={() => { setEditJob(null); setShowForm(true); }}
                className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']" data-testid="post-job-btn">
                <Plus className="w-4 h-4 mr-1.5" />Post a Job
              </Button>
            )}
          </div>
        </div>

        {/* Search & Filters Card */}
        <Card className="bg-white border-[#E5E0D5] mb-6">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9C9689]" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search jobs, businesses, locations..."
                  className="pl-10 border-[#E5E0D5] font-['Nunito']" data-testid="job-search-input" />
              </div>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-36 border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="job-sort-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="newest">Newest</SelectItem>
                  <SelectItem value="oldest">Oldest</SelectItem>
                  <SelectItem value="salary_high">Salary (High)</SelectItem>
                  <SelectItem value="salary_low">Salary (Low)</SelectItem>
                  {userLocation && <SelectItem value="distance">Nearest</SelectItem>}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => setShowFilters(!showFilters)}
                className={`border-[#E5E0D5] ${showFilters ? "bg-[var(--liturgical-primary)]/5" : ""}`} data-testid="job-filter-toggle">
                <Filter className="w-4 h-4" />
              </Button>
            </div>
            {showFilters && (
              <div className="pt-3 mt-3 border-t border-[#E5E0D5] space-y-3">
                <div className="flex flex-wrap gap-3">
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="w-44 border-[#E5E0D5] text-sm font-['Nunito']" data-testid="job-filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-36 border-[#E5E0D5] text-sm font-['Nunito'] capitalize" data-testid="job-filter-type"><SelectValue placeholder="Job Type" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all">All Types</SelectItem>
                      {jobTypes.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2">
                    <Switch checked={remoteFilter} onCheckedChange={setRemoteFilter} data-testid="job-filter-remote" />
                    <span className="text-sm font-['Nunito'] text-[#6B665E]">Remote</span>
                  </div>
                </div>
                {/* Location / Distance */}
                <div className="flex flex-wrap items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#6B665E] flex-shrink-0" />
                  <Input value={locationQuery === "Current Location" ? "" : locationQuery}
                    onChange={e => setLocationQuery(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && (e.preventDefault(), handleGeocode())}
                    placeholder="Zip or city..."
                    className="w-36 border-[#E5E0D5] font-['Nunito'] text-sm h-9" data-testid="job-location-input" />
                  <Button type="button" variant="outline" size="sm" onClick={handleGeocode}
                    disabled={geocoding || !locationQuery || locationQuery === "Current Location"}
                    className="font-['Nunito'] text-xs h-9 border-[#E5E0D5] px-2" data-testid="job-geocode-btn">
                    {geocoding ? <Loader2 className="w-3 h-3 animate-spin" /> : "Find"}
                  </Button>
                  <span className="text-xs text-[#6B665E] font-['Nunito']">or</span>
                  <Button type="button" variant={userLocation?.source === "gps" ? "default" : "outline"} size="sm"
                    onClick={handleGetLocation} disabled={gettingLocation}
                    className={`font-['Nunito'] text-xs h-9 ${userLocation?.source === "gps" ? "bg-[var(--liturgical-primary)]" : "border-[#E5E0D5]"}`}
                    data-testid="job-use-location-btn">
                    {gettingLocation ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Navigation className="w-3 h-3 mr-1" />}
                    Use My Location
                  </Button>
                  {userLocation && (
                    <>
                      <Select value={maxDistance} onValueChange={setMaxDistance}>
                        <SelectTrigger className="w-32 border-[#E5E0D5] font-['Nunito'] text-sm h-9"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="5">Within 5 mi</SelectItem>
                          <SelectItem value="10">Within 10 mi</SelectItem>
                          <SelectItem value="25">Within 25 mi</SelectItem>
                          <SelectItem value="50">Within 50 mi</SelectItem>
                          <SelectItem value="100">Within 100 mi</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button type="button" variant="ghost" size="sm" onClick={clearLocation}
                        className="font-['Nunito'] text-xs h-9 text-red-600 hover:text-red-700 px-2"><X className="w-3 h-3" /></Button>
                    </>
                  )}
                  {userLocation && (
                    <Badge className="bg-green-100 text-green-700 font-['Nunito'] text-xs">
                      <MapPin className="w-3 h-3 mr-1" />
                      {userLocation.source === "gps" ? "Using GPS" : (userLocation.display_name || locationQuery)}
                    </Badge>
                  )}
                </div>
                {hasFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}
                    className="font-['Nunito'] text-xs text-[var(--liturgical-primary)] h-8" data-testid="job-clear-filters">
                    <X className="w-3 h-3 mr-1" />Clear All Filters
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alerts */}
        {user && (hasFilters || alerts.length > 0) && (
          <div className="mb-6 space-y-3">
            {hasFilters && (
              <Button variant="outline" size="sm" onClick={handleSaveAlert} disabled={savingAlert}
                className="border-[var(--liturgical-primary)]/30 text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/5 font-['Nunito'] text-xs h-8"
                data-testid="save-alert-btn">
                {savingAlert ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Bell className="w-3 h-3 mr-1.5" />}
                Save this search as alert
              </Button>
            )}
            {alerts.length > 0 && (
              <Card className="bg-white border-[#E5E0D5]">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Bell className="w-3.5 h-3.5 text-[var(--liturgical-primary)]" />
                    <span className="text-xs font-semibold font-['Nunito'] text-[#2D2A26]">My Alerts</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {alerts.map(alert => (
                      <Badge key={alert.alert_id} variant="outline"
                        className="border-[#E5E0D5] text-[#6B665E] text-xs font-['Nunito'] cursor-pointer hover:bg-[#F9F7F2] pr-1 flex items-center gap-1"
                        data-testid={`alert-${alert.alert_id}`}>
                        <span onClick={() => applyAlertFilters(alert)}>{alert.label}</span>
                        <button onClick={e => { e.stopPropagation(); handleDeleteAlert(alert.alert_id); }}
                          className="ml-1 p-0.5 rounded hover:bg-red-100 hover:text-red-600 transition-colors"
                          data-testid={`delete-alert-${alert.alert_id}`}><X className="w-3 h-3" /></button>
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Tabs: Browse All / My Jobs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="bg-[#F0EBE0] border border-[#E5E0D5]">
            <TabsTrigger value="browse" className="font-['Nunito'] text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]"
              data-testid="job-tab-browse">
              Browse All ({total})
            </TabsTrigger>
            {user && (
              <TabsTrigger value="my-jobs" className="font-['Nunito'] text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]"
                data-testid="job-tab-my-jobs">
                My Jobs ({myJobs.length})
              </TabsTrigger>
            )}
          </TabsList>

          {/* Browse All Tab */}
          <TabsContent value="browse" className="mt-6">
            {viewMode === "map" ? (
              <div className="relative w-full h-[500px] md:h-[600px] rounded-lg overflow-hidden border border-[#E5E0D5]">
                {mapLoading && (
                  <div className="absolute inset-0 bg-white/70 z-[1000] flex items-center justify-center">
                    <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow">
                      <Loader2 className="w-4 h-4 animate-spin text-[var(--liturgical-primary)]" />
                      <span className="text-sm font-['Nunito'] text-gray-600">Loading jobs...</span>
                    </div>
                  </div>
                )}
                <div className="absolute top-3 right-3 z-[1000] bg-white rounded-lg shadow-md p-3 space-y-2">
                  <p className="text-xs font-semibold text-gray-700 font-['Nunito']">Legend</p>
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-indigo-500"></div><span className="text-xs text-gray-600 font-['Nunito']">Job</span></div>
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-teal-500"></div><span className="text-xs text-gray-600 font-['Nunito']">Remote OK</span></div>
                </div>
                <div className="absolute bottom-3 left-3 z-[1000] bg-white rounded-lg shadow-md px-3 py-2">
                  <p className="text-xs font-['Nunito'] text-gray-600"><span className="font-semibold text-gray-900">{mapMarkers.length}</span> jobs on map</p>
                </div>
                <MapContainer center={mapCenter} zoom={userLocation ? 12 : 4} className="w-full h-full" scrollWheelZoom={true}>
                  <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <MapEventHandler onBoundsChange={setMapBounds} />
                  {userLocation && <CenterOnLocation center={userLocation} />}
                  {userLocation && (
                    <Marker position={[userLocation.lat, userLocation.lng]}
                      icon={L.divIcon({
                        className: "user-location-marker",
                        html: `<div class="relative"><div class="w-6 h-6 rounded-full bg-blue-500 border-2 border-white shadow-lg flex items-center justify-center"><div class="w-2 h-2 rounded-full bg-white"></div></div><div class="absolute -inset-2 rounded-full bg-blue-500/20 animate-ping"></div></div>`,
                        iconSize: [24, 24], iconAnchor: [12, 12],
                      })}>
                      <Popup><div className="text-center p-1"><Navigation className="w-4 h-4 mx-auto text-blue-500 mb-1" /><p className="text-xs font-['Nunito'] font-semibold">Your Location</p></div></Popup>
                    </Marker>
                  )}
                  {mapMarkers.map(j => (
                    <Marker key={j.job_id} position={j.position} icon={j.icon}>
                      <Popup><JobMapPopup job={j} /></Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            ) : loading ? (
              <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[var(--liturgical-primary)]" /></div>
            ) : jobs.length === 0 ? (
              <Card className="bg-white border-[#E5E0D5]">
                <CardContent className="py-16 text-center">
                  <Briefcase className="w-12 h-12 text-[#E5E0D5] mx-auto mb-4" />
                  <h3 className="font-['Crimson_Text'] text-xl text-[#2D2A26] mb-2">
                    {hasFilters ? "No jobs match your filters" : "No jobs posted yet"}
                  </h3>
                  <p className="text-sm text-[#6B665E] font-['Nunito']">
                    {hasFilters ? "Try adjusting your search criteria" : "Be the first to post a job from your business!"}
                  </p>
                  {hasFilters && <Button variant="outline" onClick={clearFilters} className="mt-4 border-[#E5E0D5] font-['Nunito']">Clear Filters</Button>}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3" data-testid="job-list">
                {jobs.map(job => (
                  <div key={job.job_id} onClick={() => setSelectedJob(job)} className="cursor-pointer">
                    <JobCard job={job} onApply={handleApply} isOwner={myJobIds.has(job.job_id)} user={user}
                      onEdit={j => { setEditJob(j); setShowForm(true); }}
                      onDelete={j => setDeleteTarget(j)}
                      isBookmarked={bookmarkedJobIds.has(job.job_id)}
                      onToggleBookmark={handleToggleBookmark} />
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* My Jobs Tab */}
          {user && (
            <TabsContent value="my-jobs" className="mt-6">
              {myJobs.length === 0 ? (
                <Card className="bg-white border-[#E5E0D5]">
                  <CardContent className="py-12 text-center">
                    <Briefcase className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
                    <p className="text-[#6B665E] font-['Nunito']">You haven't posted any jobs yet</p>
                    {businesses.length > 0 && (
                      <Button onClick={() => { setEditJob(null); setShowForm(true); }}
                        className="mt-4 bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']">
                        <Plus className="w-4 h-4 mr-2" />Post a Job
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3" data-testid="my-job-list">
                  {myJobs.map(job => (
                    <div key={job.job_id} onClick={() => setSelectedJob(job)} className="cursor-pointer">
                      <JobCard job={job} onApply={handleApply} isOwner={true} user={user}
                        onEdit={j => { setEditJob(j); setShowForm(true); }}
                        onDelete={j => setDeleteTarget(j)}
                        isBookmarked={false} onToggleBookmark={() => {}} />
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Modals */}
      <JobFormModal isOpen={showForm} onClose={() => { setShowForm(false); setEditJob(null); }}
        onSuccess={onFormSuccess} businesses={businesses} editJob={editJob} />
      <DeleteJobModal job={deleteTarget} isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)} onConfirm={onFormSuccess} />
      <JobDetailModal job={selectedJob} isOpen={!!selectedJob}
        onClose={() => setSelectedJob(null)} onApply={handleApply}
        isOwner={selectedJob ? myJobIds.has(selectedJob.job_id) : false}
        onEdit={j => { setSelectedJob(null); setEditJob(j); setShowForm(true); }}
        onDelete={j => { setSelectedJob(null); setDeleteTarget(j); }}
        isBookmarked={selectedJob ? bookmarkedJobIds.has(selectedJob.job_id) : false}
        onToggleBookmark={handleToggleBookmark} user={user} />
    </div>
  );
}
